import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, catchError, finalize } from 'rxjs';
import { Data2Service } from 'src/app/common/data2.service';
import { DriverLedgerApiService } from '@/drivers/driver-ledger/_api/driver-ledger.api.service';
import { DriverLedgerEntry, DriverLedgerFilters } from '@/drivers/driver-ledger/_models/driver-ledger.model';
import { DriverIntegration, GratyfikantMapping } from '@/drivers/drivers-integration/_models/drivers-integration.model';
import { DriversSalaryV2ApiService } from '../_api/drivers-salary-v2.api.service';
import {
	BonusExclusionInfo,
	DOJAZD_FLAT_AMOUNT,
	DojazdEventRow,
	DriverSettlementBonus,
	DriverTourismEvent,
	DriverWorkRate,
	GratyfikantSkippedOrganization,
	NavbridgeBonusesStats,
	NawigatorPullStats,
	PERIOD_TYPE_MONTHLY,
	PRACA_FALLBACK_RATE,
	PracaEventRow,
	SalaryCacheResponse,
	TECHNICZNY_FLAT_AMOUNT,
	TechnicznyEventRow,
} from '../_models/drivers-salary-v2.model';
import {
	bonusValueToGrosze,
	countDaysInMonth,
	extractErrorMessage,
	getMonthRange,
} from '../_utils/drivers-salary-v2.utils';

export interface SettlementBonusRow extends DriverSettlementBonus {
	amountGrosze: number;
	excluded: boolean;
	exclusionComment: string | null;
}

@Injectable()
export class DriversSalaryV2StateService {
	readonly gratyfikantTotal = signal<number | null>(null);
	readonly gratyfikantLoading = signal(false);
	readonly gratyfikantLoaded = signal(false);
	readonly gratyfikantLoadedAt = signal<Date | null>(null);
	readonly gratyfikantError = signal<string | null>(null);
	readonly gratyfikantSkipped = signal<GratyfikantSkippedOrganization[]>([]);

	readonly enovaTotal = signal<number | null>(null);
	readonly enovaLoading = signal(false);
	readonly enovaLoaded = signal(false);
	readonly enovaLoadedAt = signal<Date | null>(null);
	readonly enovaError = signal<string | null>(null);

	readonly nawigatorLoading = signal(false);
	readonly nawigatorLoadedAt = signal<Date | null>(null);
	readonly nawigatorError = signal<string | null>(null);
	readonly nawigatorPullStats = signal<NawigatorPullStats | null>(null);

	readonly navbridgeBonusesLoading = signal(false);
	readonly navbridgeBonusesLoadedAt = signal<Date | null>(null);
	readonly navbridgeBonusesError = signal<string | null>(null);
	readonly navbridgeBonusesStats = signal<NavbridgeBonusesStats | null>(null);

	readonly tourismEvents = signal<DriverTourismEvent[]>([]);
	readonly tourismEventsLoading = signal(false);
	readonly eventExclusionError = signal<string | null>(null);

	readonly integration = signal<DriverIntegration | null>(null);
	readonly gratyfikantMappings = signal<GratyfikantMapping[]>([]);
	readonly integrationLoading = signal(false);

	readonly workRates = signal<DriverWorkRate[]>([]);

	readonly ledgerPending = signal<DriverLedgerEntry[]>([]);
	readonly ledgerPendingLoading = signal(false);

	readonly settlementBonuses = signal<DriverSettlementBonus[]>([]);
	readonly settlementBonusesLoading = signal(false);
	readonly bonusExclusions = signal<Record<string, BonusExclusionInfo>>({});
	readonly bonusExclusionError = signal<string | null>(null);

	readonly settlementNotes = signal<string>('');
	readonly paymentNotes = signal<string>('');
	readonly settledAt = signal<string | null>(null);
	readonly settledBy = signal<string | null>(null);
	readonly salaryId = signal<number | null>(null);
	readonly notesSaving = signal(false);
	readonly notesError = signal<string | null>(null);
	readonly settleLoading = signal(false);
	readonly settleError = signal<string | null>(null);

	readonly overrideTransferPln = signal<number | null>(null);
	readonly overrideCashPln = signal<number | null>(null);
	readonly overrideCashEur = signal<number | null>(null);
	readonly overridesSaving = signal(false);
	readonly overridesError = signal<string | null>(null);

	readonly isSettled = computed(() => this.settledAt() !== null);

	readonly selectedMonthLabel = computed(() => {
		const month = this.month();
		if (!month) return '';
		const date = new Date(`${month}-01T00:00:00`);
		return date.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
	});

	readonly activeWorkRate = computed<number | null>(() => {
		const month = this.month();
		const rates = this.workRates();
		if (!month || !rates.length) return null;

		const { monthStart, monthEnd } = getMonthRange(month);

		const matching = rates.filter((rate) => {
			const dateFrom = new Date(rate.dateFrom);
			const dateTo = rate.dateTo ? new Date(rate.dateTo) : null;
			if (dateFrom > monthEnd) return false;
			if (dateTo && dateTo < monthStart) return false;
			return true;
		});

		if (!matching.length) return null;

		const latest = matching.reduce((acc, cur) => (new Date(cur.dateFrom) > new Date(acc.dateFrom) ? cur : acc));
		const value = Number(latest.workRate);
		return Number.isFinite(value) ? Math.round(value * 100) : null;
	});

	readonly pracaEvents = computed<PracaEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);
		const rate = this.activeWorkRate() ?? PRACA_FALLBACK_RATE;

		return events
			.filter((event) => event.name === 'PRACA')
			.map((event) => {
				const days = countDaysInMonth(new Date(event.dateFrom), new Date(event.dateTo), monthStart, monthEnd);
				return {
					id: event.id,
					dateFrom: event.dateFrom,
					dateTo: event.dateTo,
					daysInMonth: days,
					amount: days * rate,
					excluded: event.excludedFromSettlement,
					comment: event.settlementExclusionComment ?? null,
				};
			});
	});

	readonly pracaTotal = computed<number | null>(() => {
		const rows = this.pracaEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	readonly pracaUsesFallbackRate = computed(() => this.activeWorkRate() === null && this.pracaEvents().length > 0);

	readonly activePeriod = computed<DriverWorkRate | null>(() => {
		const month = this.month();
		const rates = this.workRates();
		if (!month || !rates.length) return null;

		const firstDay = getMonthRange(month).dateFromStr;

		const matching = rates.filter((rate) => {
			const from = String(rate.dateFrom).slice(0, 10);
			const to = rate.dateTo ? String(rate.dateTo).slice(0, 10) : null;
			if (from > firstDay) return false;
			if (to && to < firstDay) return false;
			return true;
		});

		if (!matching.length) return null;

		return matching.reduce((acc, cur) =>
			String(cur.dateFrom).slice(0, 10) > String(acc.dateFrom).slice(0, 10) ? cur : acc,
		);
	});

	readonly isMonthlyBase = computed(() => Number(this.activePeriod()?.periodType) === PERIOD_TYPE_MONTHLY);

	readonly monthlyBaseAmount = computed<number | null>(() => {
		const period = this.activePeriod();
		if (Number(period?.periodType) !== PERIOD_TYPE_MONTHLY) return null;
		const amount = Number(period?.monthlyAmount);
		return Number.isFinite(amount) && amount > 0 ? amount : 0;
	});

	readonly pracaBaseGrosze = computed<number>(() =>
		this.isMonthlyBase() ? (this.monthlyBaseAmount() ?? 0) : (this.pracaTotal() ?? 0),
	);

	readonly technicznyEvents = computed<TechnicznyEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);

		return events
			.filter((event) => event.name === 'TECHNICZNY')
			.filter(
				(event) => countDaysInMonth(new Date(event.dateFrom), new Date(event.dateTo), monthStart, monthEnd) > 0,
			)
			.map((event) => ({
				id: event.id,
				dateFrom: event.dateFrom,
				dateTo: event.dateTo,
				amount: TECHNICZNY_FLAT_AMOUNT,
				excluded: event.excludedFromSettlement,
				comment: event.settlementExclusionComment ?? null,
			}));
	});

	readonly technicznyTotal = computed<number | null>(() => {
		const rows = this.technicznyEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	readonly dojazdEvents = computed<DojazdEventRow[]>(() => {
		const events = this.tourismEvents();
		const month = this.month();
		if (!month) return [];

		const { monthStart, monthEnd } = getMonthRange(month);

		return events
			.filter((event) => event.name === 'DOJAZD')
			.filter(
				(event) => countDaysInMonth(new Date(event.dateFrom), new Date(event.dateTo), monthStart, monthEnd) > 0,
			)
			.map((event) => ({
				id: event.id,
				dateFrom: event.dateFrom,
				dateTo: event.dateTo,
				amount: DOJAZD_FLAT_AMOUNT,
				excluded: event.excludedFromSettlement,
				comment: event.settlementExclusionComment ?? null,
			}));
	});

	readonly dojazdTotal = computed<number | null>(() => {
		const rows = this.dojazdEvents();
		if (!rows.length) return null;
		return rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.amount, 0);
	});

	readonly ledgerPendingTotals = computed<{ pln: number; eur: number }>(() => {
		const entries = this.ledgerPending();
		const netAmount = (entry: DriverLedgerEntry, target: 'PLN' | 'EUR'): number => {
			const gross = target === 'PLN' ? (entry.amountPln ?? 0) : (entry.amountEur ?? 0);
			if (!entry.commission || !entry.amount) return gross;
			return gross - Math.round((entry.commission * gross) / entry.amount);
		};
		const pln = entries.filter((e) => e.targetCurrency === 'PLN').reduce((sum, e) => sum + netAmount(e, 'PLN'), 0);
		const eur = entries.filter((e) => e.targetCurrency === 'EUR').reduce((sum, e) => sum + netAmount(e, 'EUR'), 0);
		return { pln, eur };
	});

	readonly settlementRows = computed<SettlementBonusRow[]>(() => {
		const exclusions = this.bonusExclusions();
		return this.settlementBonuses().map((b) => {
			const exclusion = exclusions[String(b.id)];
			return {
				...b,
				amountGrosze: bonusValueToGrosze(b.value),
				excluded: exclusion?.excludedFromSettlement ?? false,
				exclusionComment: exclusion?.settlementExclusionComment ?? null,
			};
		});
	});

	readonly settlementPositive = computed<SettlementBonusRow[]>(() =>
		this.settlementRows().filter((b) => b.amountGrosze >= 0),
	);

	readonly settlementNegative = computed<SettlementBonusRow[]>(() =>
		this.settlementRows().filter((b) => b.amountGrosze < 0),
	);

	readonly settlementPositiveTotal = computed(() =>
		this.settlementPositive()
			.filter((b) => !b.excluded)
			.reduce((sum, b) => sum + b.amountGrosze, 0),
	);

	readonly settlementNegativeTotal = computed(() =>
		this.settlementNegative()
			.filter((b) => !b.excluded)
			.reduce((sum, b) => sum + b.amountGrosze, 0),
	);

	readonly nawigatorTotalPln = computed(
		() => this.pracaBaseGrosze() + (this.technicznyTotal() ?? 0) + (this.dojazdTotal() ?? 0),
	);

	readonly settlementNetPln = computed(() =>
		this.settlementRows()
			.filter((b) => !b.excluded && (!b.currency || b.currency === 'PLN'))
			.reduce((sum, b) => sum + b.amountGrosze, 0),
	);

	readonly settlementNetEur = computed(() =>
		this.settlementRows()
			.filter((b) => !b.excluded && b.currency === 'EUR')
			.reduce((sum, b) => sum + b.amountGrosze, 0),
	);

	readonly effectiveDietaPln = computed(() => (this.enovaTotal() ?? 0) - (this.gratyfikantTotal() ?? 0));

	readonly sumAllPln = computed<number | null>(() => {
		const nawigator = this.nawigatorTotalPln();
		const dieta = this.effectiveDietaPln();
		const gratyfikant = this.gratyfikantTotal() ?? 0;
		const settlements = this.settlementNetPln();
		const kasa = this.ledgerPendingTotals().pln;
		if (nawigator === 0 && dieta === 0 && gratyfikant === 0 && settlements === 0 && kasa === 0) return null;
		return nawigator - dieta - gratyfikant + settlements - kasa;
	});

	readonly sumAllEur = computed<number | null>(() => {
		const settlements = this.settlementNetEur();
		const kasa = this.ledgerPendingTotals().eur;
		if (settlements === 0 && kasa === 0) return null;
		return settlements - kasa;
	});

	readonly effectiveCashPln = computed<number | null>(() => this.overrideCashPln());

	readonly effectiveTransferPln = computed<number | null>(() => {
		const override = this.overrideTransferPln();
		if (override !== null) return override;
		const sum = this.sumAllPln();
		if (sum === null) return null;
		const cash = this.effectiveCashPln() ?? 0;
		return sum - cash;
	});

	readonly effectiveCashEur = computed<number | null>(() => {
		const override = this.overrideCashEur();
		if (override !== null) return override;
		return this.sumAllEur();
	});

	readonly plnSplitMismatch = computed(() => {
		const sum = this.sumAllPln();
		if (sum === null) return false;
		const transfer = this.effectiveTransferPln() ?? 0;
		const cash = this.effectiveCashPln() ?? 0;
		return Math.abs(sum - (transfer + cash)) > 1;
	});

	readonly eurSplitMismatch = computed(() => {
		const sum = this.sumAllEur();
		if (sum === null) return false;
		const cash = this.effectiveCashEur() ?? 0;
		return Math.abs(sum - cash) > 1;
	});

	readonly hasActiveGratyfikantMapping = computed(() => {
		const mappings = this.gratyfikantMappings();
		const month = this.month();
		if (!mappings.length || !month) return false;

		const { monthStart, monthEnd } = getMonthRange(month);

		return mappings.some((mapping) => {
			const dateFrom = new Date(mapping.date_from);
			const dateTo = mapping.date_to ? new Date(mapping.date_to) : null;
			if (dateFrom > monthEnd) return false;
			if (dateTo && dateTo < monthStart) return false;
			return true;
		});
	});

	readonly gratyfikantDisabled = computed(() => this.gratyfikantLoading() || !this.hasActiveGratyfikantMapping());
	readonly gratyfikantTooltip = computed(() => {
		const loading = this.integrationLoading() || this.gratyfikantLoading();
		if (loading) return '';
		if (!this.hasActiveGratyfikantMapping()) {
			return 'Brak konfiguracji Gratyfikant dla tego kierowcy w wybranym miesiącu. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly nawigatorDisabled = computed(
		() => this.nawigatorLoading() || this.integrationLoading() || !this.integration()?.nawigator_id,
	);
	readonly nawigatorTooltip = computed(() => {
		if (this.integrationLoading()) return '';
		if (!this.integration()?.nawigator_id) {
			return 'Brak ID Nawigator dla tego kierowcy. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly enovaDisabled = computed(() => this.integrationLoading() || !this.integration()?.enova_id);
	readonly enovaTooltip = computed(() => {
		if (this.integrationLoading()) return '';
		if (!this.integration()?.enova_id) {
			return 'Brak ID Enova dla tego kierowcy. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly navbridgeBonusesDisabled = computed(
		() => this.navbridgeBonusesLoading() || this.integrationLoading() || !this.integration()?.nawigator_id,
	);
	readonly navbridgeBonusesTooltip = computed(() => {
		if (this.integrationLoading()) return '';
		if (!this.integration()?.nawigator_id) {
			return 'Brak ID Nawigator dla tego kierowcy. Skonfiguruj w zakładce Integracje.';
		}
		return '';
	});

	readonly selectedDriverId = computed(() => this.driverId());
	readonly selectedMonth = computed(() => this.month());

	private readonly destroyRef = inject(DestroyRef);
	private readonly api = inject(Data2Service);
	private readonly ledgerApi = inject(DriverLedgerApiService);
	private readonly apiService = inject(DriversSalaryV2ApiService);

	private readonly driverId = signal<string>('');
	private readonly month = signal<string>('');

	setContext(driverId: string, month: string): void {
		if (this.driverId() === driverId && this.month() === month) return;
		this.driverId.set(driverId);
		this.month.set(month);
		this.resetData();
	}

	reload(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		this.loadSalaryFromCache();
		this.loadIntegrations();
		this.loadTourismEvents();
		this.loadWorkRates();
		this.loadSettlementBonuses();
		this.loadSettlementBonusExclusions();
	}

	loadGratyfikant(): void {
		this.gratyfikantLoading.set(true);
		this.gratyfikantError.set(null);
		this.gratyfikantSkipped.set([]);
		this.apiService
			.getGratyfikantSalaries(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.gratyfikantError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Gratyfikant.'));
					return EMPTY;
				}),
				finalize(() => this.gratyfikantLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				const totalZl = response.data.reduce((sum, row) => sum + (Number(row['Netto']) || 0), 0);
				this.gratyfikantTotal.set(Math.round(totalZl * 100));
				this.gratyfikantSkipped.set(response.meta?.skipped ?? []);
				this.gratyfikantLoaded.set(true);
				this.gratyfikantLoadedAt.set(new Date());
			});
	}

	loadNawigator(): void {
		this.nawigatorLoading.set(true);
		this.nawigatorError.set(null);
		this.apiService
			.pullNawigatorEvents(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.nawigatorError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Nawigator.'));
					return EMPTY;
				}),
				finalize(() => this.nawigatorLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((stats) => {
				this.nawigatorPullStats.set(stats);
				this.nawigatorLoadedAt.set(new Date());
				this.loadTourismEvents();
			});
	}

	loadEnova(): void {
		this.enovaLoading.set(true);
		this.enovaError.set(null);
		this.apiService
			.getEnovaSalaries(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.enovaError.set(extractErrorMessage(err, 'Nie udało się pobrać danych z Enova.'));
					return EMPTY;
				}),
				finalize(() => this.enovaLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				const totalZl = data.reduce((sum, row) => sum + (Number(row['amount']) || 0), 0);
				this.enovaTotal.set(Math.round(totalZl * 100));
				this.enovaLoaded.set(true);
				this.enovaLoadedAt.set(new Date());
			});
	}

	loadNavbridgeBonuses(): void {
		this.navbridgeBonusesLoading.set(true);
		this.navbridgeBonusesError.set(null);
		this.apiService
			.pullNavbridgeBonuses(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.navbridgeBonusesError.set(
						extractErrorMessage(err, 'Nie udało się pobrać dodatków z NavBridge.'),
					);
					return EMPTY;
				}),
				finalize(() => this.navbridgeBonusesLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((stats) => {
				this.navbridgeBonusesStats.set(stats);
				this.navbridgeBonusesLoadedAt.set(new Date());
				this.refreshSettlementBonuses();
			});
	}

	refreshSettlementBonuses(): void {
		this.loadSettlementBonuses();
		this.loadSettlementBonusExclusions();
	}

	setEventExclusion(eventId: string, excluded: boolean, comment: string | null): void {
		if (this.isSettled()) return;

		const snapshot = this.tourismEvents();
		const normalizedComment = comment && comment.trim() !== '' ? comment : null;

		this.tourismEvents.set(
			snapshot.map((event) =>
				event.id === eventId
					? { ...event, excludedFromSettlement: excluded, settlementExclusionComment: normalizedComment }
					: event,
			),
		);
		this.eventExclusionError.set(null);

		this.apiService
			.setEventExclusion(this.driverId(), eventId, { excluded, comment: normalizedComment })
			.pipe(
				catchError((err) => {
					this.tourismEvents.set(snapshot);
					this.eventExclusionError.set(extractErrorMessage(err, 'Nie udało się zapisać wykluczenia wpisu.'));
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				this.tourismEvents.set(
					this.tourismEvents().map((event) =>
						event.id === eventId
							? {
									...event,
									excludedFromSettlement: response.excludedFromSettlement,
									settlementExclusionComment: response.settlementExclusionComment,
								}
							: event,
					),
				);
			});
	}

	setBonusExclusion(bonusId: string, excluded: boolean, comment: string | null): void {
		if (this.isSettled()) return;

		const snapshot = this.bonusExclusions();
		const normalizedComment = comment && comment.trim() !== '' ? comment : null;

		this.bonusExclusions.set({
			...snapshot,
			[bonusId]: {
				...snapshot[bonusId],
				bonusId: Number(bonusId),
				excludedFromSettlement: excluded,
				settlementExclusionComment: normalizedComment,
				updatedAt: snapshot[bonusId]?.updatedAt ?? null,
				updatedBy: snapshot[bonusId]?.updatedBy ?? null,
			},
		});
		this.bonusExclusionError.set(null);

		this.apiService
			.setBonusExclusion(this.driverId(), bonusId, { excluded, comment: normalizedComment })
			.pipe(
				catchError((err) => {
					this.bonusExclusions.set(snapshot);
					this.bonusExclusionError.set(
						extractErrorMessage(err, 'Nie udało się zapisać wykluczenia dodatku.'),
					);
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				this.bonusExclusions.set({
					...this.bonusExclusions(),
					[String(response.bonusId)]: response,
				});
			});
	}

	saveNotes(): void {
		if (this.isSettled() || this.notesSaving()) return;

		this.notesSaving.set(true);
		this.notesError.set(null);

		this.apiService
			.saveNotes(this.driverId(), this.month(), {
				settlement_notes: this.settlementNotes(),
				payment_notes: this.paymentNotes(),
			})
			.pipe(
				catchError((err) => {
					this.notesError.set(extractErrorMessage(err, 'Nie udało się zapisać uwag.'));
					return EMPTY;
				}),
				finalize(() => this.notesSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.applySalaryCacheResponse(data));
	}

	saveOverrides(): void {
		if (this.isSettled() || this.overridesSaving()) return;

		this.overridesSaving.set(true);
		this.overridesError.set(null);

		this.apiService
			.saveOverrides(this.driverId(), this.month(), {
				transfer_pln: this.overrideTransferPln(),
				cash_pln: this.overrideCashPln(),
				cash_eur: this.overrideCashEur(),
			})
			.pipe(
				catchError((err) => {
					this.overridesError.set(extractErrorMessage(err, 'Nie udało się zapisać korekty.'));
					return EMPTY;
				}),
				finalize(() => this.overridesSaving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.applySalaryCacheResponse(data));
	}

	settleMonth(): void {
		if (this.isSettled() || this.settleLoading()) return;

		this.settleLoading.set(true);
		this.settleError.set(null);

		this.apiService
			.settleMonth(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.settleError.set(extractErrorMessage(err, 'Nie udało się rozliczyć miesiąca.'));
					return EMPTY;
				}),
				finalize(() => this.settleLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				this.applySalaryCacheResponse(data);
				this.loadPendingLedger();
			});
	}

	unsettleMonth(): void {
		if (!this.isSettled() || this.settleLoading()) return;

		this.settleLoading.set(true);
		this.settleError.set(null);

		this.apiService
			.unsettleMonth(this.driverId(), this.month())
			.pipe(
				catchError((err) => {
					this.settleError.set(extractErrorMessage(err, 'Nie udało się cofnąć rozliczenia.'));
					return EMPTY;
				}),
				finalize(() => this.settleLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => {
				if (data) {
					this.applySalaryCacheResponse(data);
				} else {
					this.settledAt.set(null);
					this.settledBy.set(null);
				}
				this.loadPendingLedger();
			});
	}

	private resetData(): void {
		this.gratyfikantTotal.set(null);
		this.gratyfikantLoading.set(false);
		this.gratyfikantLoaded.set(false);
		this.gratyfikantLoadedAt.set(null);
		this.gratyfikantError.set(null);
		this.gratyfikantSkipped.set([]);
		this.enovaTotal.set(null);
		this.enovaLoading.set(false);
		this.enovaLoaded.set(false);
		this.enovaLoadedAt.set(null);
		this.enovaError.set(null);
		this.nawigatorLoading.set(false);
		this.nawigatorLoadedAt.set(null);
		this.nawigatorError.set(null);
		this.nawigatorPullStats.set(null);
		this.navbridgeBonusesLoading.set(false);
		this.navbridgeBonusesLoadedAt.set(null);
		this.navbridgeBonusesError.set(null);
		this.navbridgeBonusesStats.set(null);
		this.tourismEvents.set([]);
		this.tourismEventsLoading.set(false);
		this.eventExclusionError.set(null);
		this.integration.set(null);
		this.gratyfikantMappings.set([]);
		this.integrationLoading.set(false);
		this.workRates.set([]);
		this.ledgerPending.set([]);
		this.ledgerPendingLoading.set(false);
		this.settlementBonuses.set([]);
		this.settlementBonusesLoading.set(false);
		this.bonusExclusions.set({});
		this.bonusExclusionError.set(null);
		this.settlementNotes.set('');
		this.paymentNotes.set('');
		this.settledAt.set(null);
		this.settledBy.set(null);
		this.salaryId.set(null);
		this.notesSaving.set(false);
		this.notesError.set(null);
		this.settleLoading.set(false);
		this.settleError.set(null);
		this.overrideTransferPln.set(null);
		this.overrideCashPln.set(null);
		this.overrideCashEur.set(null);
		this.overridesSaving.set(false);
		this.overridesError.set(null);
	}

	private loadPendingLedger(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		const { dateToStr } = getMonthRange(month);
		const settledSalaryId = this.isSettled() ? this.salaryId() : null;
		const filters: DriverLedgerFilters = settledSalaryId
			? { page: 1, perPage: 100, status: 'SETTLED', settledBySalaryId: settledSalaryId }
			: { page: 1, perPage: 100, status: 'NOT_SETTLED', dateTo: dateToStr };

		this.ledgerPendingLoading.set(true);
		this.ledgerApi
			.getLedger(driverId, filters)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.ledgerPendingLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => this.ledgerPending.set(response.items ?? []));
	}

	private loadSettlementBonuses(): void {
		const driverId = this.driverId();
		const month = this.month();
		if (!driverId || !month) return;

		const { dateFromStr, dateToStr } = getMonthRange(month);

		this.settlementBonusesLoading.set(true);
		const url =
			`/settlements/settlementBonus?ownerId=${encodeURIComponent(driverId)}` +
			`&date_from=${dateFromStr}&date_to=${dateToStr}`;
		this.api.get<{ data: DriverSettlementBonus[] }>(url, (result, error) => {
			this.settlementBonusesLoading.set(false);
			if (error || !result) {
				this.settlementBonuses.set([]);
				return;
			}
			this.settlementBonuses.set(result.data ?? []);
		});
	}

	private loadSettlementBonusExclusions(): void {
		const driverId = this.driverId();
		if (!driverId) return;

		this.apiService
			.getSettlementBonusExclusions(driverId)
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((list) => {
				this.bonusExclusions.set(Object.fromEntries(list.map((e) => [String(e.bonusId), e])));
			});
	}

	private loadWorkRates(): void {
		this.api.get(
			`/driver/driverRate/?driver=${this.driverId()}`,
			(result: false | { data: DriverWorkRate[] }, error: unknown) => {
				if (error || !result) return;
				this.workRates.set(result.data ?? []);
			},
		);
	}

	private loadSalaryFromCache(): void {
		this.apiService
			.getSalaryCache(this.driverId(), this.month())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.loadPendingLedger()),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((data) => this.applySalaryCacheResponse(data));
	}

	private applySalaryCacheResponse(data: SalaryCacheResponse | null): void {
		if (data?.gratyfikant_netto) {
			this.gratyfikantTotal.set(data.gratyfikant_netto);
			this.gratyfikantLoaded.set(true);
		}
		if (data?.enova_netto) {
			this.enovaTotal.set(data.enova_netto);
			this.enovaLoaded.set(true);
		}
		this.settlementNotes.set(data?.settlement_notes ?? '');
		this.paymentNotes.set(data?.payment_notes ?? '');
		this.settledAt.set(data?.settled_at ?? null);
		this.settledBy.set(data?.settled_by ?? null);
		this.salaryId.set(data?.id ?? null);
		this.overrideTransferPln.set(data?.override_transfer_pln ?? null);
		this.overrideCashPln.set(data?.override_cash_pln ?? null);
		this.overrideCashEur.set(data?.override_cash_eur ?? null);
	}

	private loadTourismEvents(): void {
		this.tourismEventsLoading.set(true);
		this.apiService
			.getTourismEvents(this.driverId(), this.month())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.tourismEventsLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((events) => this.tourismEvents.set(events));
	}

	private loadIntegrations(): void {
		this.integrationLoading.set(true);

		this.apiService
			.getIntegration(this.driverId())
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.integrationLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((integration) => this.integration.set(integration));

		this.apiService
			.getGratyfikantMappings(this.driverId())
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((mappings) => this.gratyfikantMappings.set(mappings));
	}
}
